import type {
  QuestionnaireBranding,
  QuestionnaireBuilderPage,
  QuestionnaireEmailTemplate,
  QuestionnaireSettings,
} from '../types';

export const QUESTIONNAIRE_DEFAULT_BRANDING: QuestionnaireBranding = {
  foregroundColor: '#ffffff',
  textColor: '#2f3337',
  buttonColor: '#0d9ddb',
  borderColor: '#d8dde2',
  linkColor: '#0d6efd',
  headerImageUrl: '',
  footerImageUrl: '',
};

export const QUESTIONNAIRE_DEFAULT_SETTINGS: QuestionnaireSettings = {
  description: '',
  title: '',
  invitationTimingDays: 1,
  automaticThankYou: true,
  reminderCount: 1,
  reminderTimingDays: 3,
};

export const QUESTIONNAIRE_EMAIL_PLACEHOLDERS: ReadonlyArray<{
  readonly token: string;
  readonly label: string;
}> = [
  { token: '[#FIRSTNAME#]', label: 'First name' },
  { token: '[#LASTNAME#]', label: 'Last name' },
  { token: '[#INVITATION_NAME#]', label: 'First and last name' },
  { token: '[#TITLE#]', label: 'Title' },
  { token: '[#SURVEYLINK#]', label: 'Link to questionnaire' },
  { token: '[#ACCOUNTNAME#]', label: 'Account name' },
];

export const QUESTIONNAIRE_DEFAULT_EMAILS: ReadonlyArray<QuestionnaireEmailTemplate> = [
  {
    kind: 'invitation',
    language: 'English',
    subject: 'How was your stay?',
    senderName: 'The Langdon',
    freestyle: false,
    content:
      'Dear [#FIRSTNAME#],\n\nThank you for staying with us. We would love to hear about your experience — it takes less than two minutes.\n\n[#SURVEYLINK#]\n\nKind regards,\n[#ACCOUNTNAME#]',
  },
  {
    kind: 'reminder',
    language: 'English',
    subject: 'A gentle reminder — tell us about your stay',
    senderName: 'The Langdon',
    freestyle: false,
    content:
      'Dear [#FIRSTNAME#],\n\nWe noticed you have not had a chance to share your feedback yet. Your thoughts help us improve.\n\n[#SURVEYLINK#]\n\nKind regards,\n[#ACCOUNTNAME#]',
  },
  {
    kind: 'thank-you',
    language: 'English',
    subject: 'Thank you for your feedback',
    senderName: 'The Langdon',
    freestyle: false,
    content:
      'Dear [#FIRSTNAME#],\n\nThank you for taking the time to tell us about your stay. Your feedback has been shared with our team.\n\nKind regards,\n[#ACCOUNTNAME#]',
  },
  {
    kind: 'management-response',
    language: 'English',
    subject: 'A response from our management team',
    senderName: 'The Langdon',
    freestyle: false,
    content:
      'Dear [#FIRSTNAME#],\n\nThank you for your recent feedback. We wanted to respond personally.\n\nKind regards,\n[#ACCOUNTNAME#]',
  },
];

function choices(...labels: string[]) {
  return labels.map((label, index) => ({
    id: `choice-${index + 1}-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    label,
    value: label.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
  }));
}

const EMPTY_ELEMENT = {
  helpText: '',
  required: false,
  choices: [],
  rows: [],
  placeholder: '',
  content: '',
  headlineLevel: 2 as const,
  imageUrl: '',
  imageAlt: '',
  parameterKey: '',
};

export const QUESTIONNAIRE_SAMPLE_PAGES: ReadonlyArray<QuestionnaireBuilderPage> = [
  {
    id: 'page-1',
    name: 'Your stay',
    kind: 'normal',
    elements: [
      {
        ...EMPTY_ELEMENT,
        id: 'el-1',
        kind: 'headline',
        label: 'Tell us about your stay',
        headlineLevel: 1,
      },
      {
        ...EMPTY_ELEMENT,
        id: 'el-2',
        kind: 'text',
        label: 'Intro',
        content:
          'Your feedback helps us make every stay better. This should take about two minutes.',
      },
      {
        ...EMPTY_ELEMENT,
        id: 'el-3',
        kind: 'radio',
        label: 'How likely are you to recommend us to a friend or colleague?',
        helpText: 'Where 1 is not at all likely and 5 is extremely likely.',
        required: true,
        choices: choices('1', '2', '3', '4', '5'),
      },
      {
        ...EMPTY_ELEMENT,
        id: 'el-4',
        kind: 'radio-grid',
        label: 'How would you rate the following?',
        required: true,
        rows: choices('Room comfort', 'Cleanliness', 'Staff friendliness', 'Value for money'),
        choices: choices('Poor', 'Fair', 'Good', 'Very good', 'Excellent'),
      },
    ],
  },
  {
    id: 'page-2',
    name: 'Your feedback',
    kind: 'normal',
    elements: [
      {
        ...EMPTY_ELEMENT,
        id: 'el-5',
        kind: 'checkbox',
        label: 'Which facilities did you use?',
        choices: choices('Restaurant', 'Spa', 'Gym', 'Pool', 'Conference rooms'),
      },
      {
        ...EMPTY_ELEMENT,
        id: 'el-6',
        kind: 'long-answer',
        label: 'Is there anything else you would like to share?',
        placeholder: 'Your comments help us improve.',
      },
      { ...EMPTY_ELEMENT, id: 'el-7', kind: 'horizontal-rule', label: 'Divider' },
      {
        ...EMPTY_ELEMENT,
        id: 'el-8',
        kind: 'hidden-parameter',
        label: 'Booking reference',
        parameterKey: 'booking_ref',
      },
    ],
  },
  {
    id: 'page-3',
    name: 'Thank you',
    kind: 'thank-you',
    elements: [
      {
        ...EMPTY_ELEMENT,
        id: 'el-9',
        kind: 'headline',
        label: 'Thank you for your feedback',
        headlineLevel: 2,
      },
      {
        ...EMPTY_ELEMENT,
        id: 'el-10',
        kind: 'text',
        label: 'Outro',
        content: 'We have shared your comments with our team. We hope to welcome you back soon.',
      },
    ],
  },
];
